// 用戶活動追蹤服務
import DeviceInfoService, { DeviceInfo } from './deviceInfoService';

// 添加調試日誌
console.log('🔍 userActivityService.ts 已載入');
console.log('🔍 DeviceInfoService:', DeviceInfoService);

class UserActivityService {
  private static instance: UserActivityService;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private readonly HEARTBEAT_INTERVAL = 60000; // 60秒發送一次心跳（減少頻率）
  private readonly INACTIVE_THRESHOLD = 300000; // 5分鐘無活動視為離線
  private lastRequestTime = 0; // 記錄上次請求時間
  private readonly MIN_REQUEST_INTERVAL = 5000; // 最小請求間隔 5 秒
  private deviceInfoService: DeviceInfoService;

  private constructor() {
    console.log('🔍 UserActivityService 構造函數被調用');
    try {
      this.deviceInfoService = DeviceInfoService.getInstance();
      console.log('🔍 DeviceInfoService 實例創建成功:', this.deviceInfoService);
    } catch (error) {
      console.error('❌ DeviceInfoService 實例創建失敗:', error);
    }
  }

  public static getInstance(): UserActivityService {
    console.log('🔍 UserActivityService.getInstance() 被調用');
    if (!UserActivityService.instance) {
      console.log('🔍 創建新的 UserActivityService 實例');
      UserActivityService.instance = new UserActivityService();
    } else {
      console.log('🔍 使用現有的 UserActivityService 實例');
    }
    return UserActivityService.instance;
  }

  // 開始追蹤用戶活動
  public startTracking(userId: string, userName: string, userEmail: string): void {
    console.log('開始追蹤用戶活動:', { userId, userName, userEmail });
    
    // 停止現有的心跳
    this.stopTracking();

    // 測試設備信息收集
    try {
      const deviceInfo = this.deviceInfoService.collectDeviceInfo();
      console.log('🔍 在 startTracking 中收集到的設備信息:', deviceInfo);
    } catch (error) {
      console.error('❌ 設備信息收集失敗:', error);
    }

    // 發送初始登入狀態
    this.updateUserOnlineStatus(userId, userName, userEmail, 'login');

    // 開始心跳檢測，傳遞用戶名稱和郵箱
    this.startHeartbeat(userId, userName, userEmail);
  }

  // 停止追蹤用戶活動
  public stopTracking(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  // 更新用戶在線狀態
  private async updateUserOnlineStatus(userId: string, userName: string, userEmail: string, action: string = 'heartbeat'): Promise<void> {
    // 檢查請求頻率限制
    const now = Date.now();
    if (now - this.lastRequestTime < this.MIN_REQUEST_INTERVAL) {
      console.log('跳過請求，距離上次請求時間太短');
      return;
    }
    this.lastRequestTime = now;

    // 收集設備信息
    const deviceInfo = this.deviceInfoService.collectDeviceInfo();
    console.log('🔍 收集到的設備信息:', deviceInfo);

    try {
      const response = await fetch('/api/admin/update-activity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          userName,
          userEmail,
          action,
          deviceInfo
        })
      });

      if (!response.ok) {
        // 在開發環境中，如果 API 不存在，只記錄警告而不顯示錯誤
        if (response.status === 404) {
          console.log('ℹ️ 用戶活動追蹤 API 不可用（開發環境）');
        } else {
          console.error('更新用戶在線狀態失敗:', response.statusText);
        }
      }
    } catch (error) {
      // 🔧 修復：靜默處理開發環境的 API 錯誤，不影響用戶體驗
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.log('ℹ️ 開發環境：後端 API 不可用，跳過用戶活動追蹤');
      } else {
        console.warn('更新用戶在線狀態錯誤:', error instanceof Error ? error.message : 'Unknown error');
      }
    }
  }

  // 開始心跳檢測
  private startHeartbeat(userId: string, userName: string, userEmail: string): void {
    this.heartbeatInterval = setInterval(async () => {
      // 檢查請求頻率限制
      const now = Date.now();
      if (now - this.lastRequestTime < this.MIN_REQUEST_INTERVAL) {
        console.log('跳過心跳請求，距離上次請求時間太短');
        return;
      }
      this.lastRequestTime = now;

      try {
        const response = await fetch('/api/admin/update-activity', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            userName,
            userEmail,
            action: 'heartbeat'
          })
        });

        if (!response.ok) {
          // 在開發環境中，如果 API 不存在，只記錄警告而不顯示錯誤
          if (response.status === 404) {
            console.log('ℹ️ 用戶活動追蹤 API 不可用（開發環境）');
          } else {
            console.error('心跳檢測失敗:', response.statusText);
          }
        }
      } catch (error) {
        // 🔧 修復：靜默處理開發環境的心跳檢測錯誤
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          console.log('ℹ️ 開發環境：心跳檢測 API 不可用');
        } else {
          console.warn('心跳檢測錯誤:', error instanceof Error ? error.message : 'Unknown error');
        }
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  // 記錄用戶活動
  public recordActivity(userId: string): void {
    // 可以記錄具體的活動類型，如播放影片、創建重點等
    this.updateUserActivity(userId);
  }

  // 更新用戶活動時間
  private async updateUserActivity(userId: string): Promise<void> {
    // 檢查請求頻率限制
    const now = Date.now();
    if (now - this.lastRequestTime < this.MIN_REQUEST_INTERVAL) {
      console.log('跳過活動更新請求，距離上次請求時間太短');
      return;
    }
    this.lastRequestTime = now;

    try {
      // 檢查是否在開發環境
      const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      if (isDevelopment) {
        // 開發環境：跳過 API 調用，避免 404 錯誤
        console.log('ℹ️ 開發環境：跳過用戶活動追蹤 API 調用');
        return;
      }

      const response = await fetch('/api/admin/update-activity', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          action: 'activity'
        })
      });

      if (!response.ok) {
        console.error('更新用戶活動失敗:', response.statusText);
      }
    } catch (error) {
      // 靜默處理錯誤，避免影響用戶體驗
      console.log('ℹ️ 用戶活動追蹤暫時不可用');
    }
  }

  // 獲取在線用戶列表
  public async getOnlineUsers(): Promise<any[]> {
    try {
      const response = await fetch('/api/admin/online-users', {
        method: 'GET',
        headers: {
        }
      });

      if (!response.ok) {
        throw new Error('獲取在線用戶失敗');
      }

      const data = await response.json();
      return data.onlineUsers || [];
    } catch (error) {
      console.error('獲取在線用戶錯誤:', error);
      return [];
    }
  }
}

export default UserActivityService;
